// The bridge between a reading document's offsets and the rendered DOM. Both
// readers — lekterion's `ReadingBody` and retells' `DocumentPane` — emit every
// text run as an element carrying `data-start` (its base offset into the
// immutable document `text`), so these helpers convert a DOM Selection ↔ text
// offsets in both directions: a drag/tap to `{start, end}`, and an offset back
// to a DOM point (to re-select a `?sel` span, a tapped word, or scroll to a
// locus). This is retells' copy, the strict superset of lekterion's: the
// optional `RunScope` (with its `ORIGINAL_SCOPE` value) is the only addition,
// and omitting it gives lekterion's behaviour unchanged. Bindery is canonical
// for this module.

export interface OffsetRange {
  start: number;
  end: number;
}

export interface DomPoint {
  node: Text;
  offset: number;
}

/**
 * A CSS selector every resolved run must sit inside, or `null` for "anywhere
 * under the root".
 *
 * `data-start` is a *model-relative* offset, so a run is only meaningful next to
 * the model it was rendered from. When one root holds runs from two models —
 * the mobile interleaved column, where original stretches are inset between
 * runs of the retelling — resolving without a scope would hand the caller a
 * retelling offset interpreted against the original's text: not an error, just
 * silently wrong glosses. Callers pass the subtree marker of the model they
 * hold (`[data-pane="original"]`), which is a no-op on the desktop panes, where
 * the root already IS that subtree.
 */
export type RunScope = string | null;

/**
 * The one `RunScope` the reader actually uses: the marker every original-text
 * subtree carries (`DocumentPane`'s original pane, each mobile inset). Lives
 * here, next to the resolvers it parameterizes, because this module is the one
 * every consumer already imports and it imports none of them — the selection
 * layer, the word-navigation mode and the copy handler all held their own copy
 * of this string, and one of them spelled it `ORIGINAL_SUBTREE`, so a grep for
 * the name would not even have found all three.
 */
export const ORIGINAL_SCOPE = '[data-pane="original"]';

/** The nearest in-scope `[data-start]` ancestor element of a DOM node, if any. */
function enclosingRun(node: Node, scope: RunScope): HTMLElement | null {
  const el =
    node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
  const run = el?.closest<HTMLElement>("[data-start]") ?? null;
  if (!run) return null;
  return scope === null || run.closest(scope) ? run : null;
}

/** A DOM point inside a known run → its document offset. */
function offsetInRun(
  run: HTMLElement,
  node: Node,
  offset: number,
): number | null {
  if (!run.dataset.start) return null;
  const base = Number(run.dataset.start);
  if (Number.isNaN(base)) return null;
  if (node.nodeType === Node.TEXT_NODE) return base + offset;
  // Container is the element itself: `offset` indexes children — clamp to the
  // run's text extent (start when 0, end otherwise).
  return base + (offset > 0 ? (run.textContent?.length ?? 0) : 0);
}

function pointToOffset(
  node: Node,
  offset: number,
  scope: RunScope,
): number | null {
  const run = enclosingRun(node, scope);
  return run === null ? null : offsetInRun(run, node, offset);
}

/**
 * A non-collapsed Selection fully inside `root` (and inside ONE `scope`
 * element, when given) → document offsets, else null. A selection that starts
 * in a scoped subtree and ends outside it resolves to null rather than to a
 * mixed-model range — and so does one whose endpoints sit in two DIFFERENT
 * scoped subtrees. The latter matters in the mobile interleaved column: a drag
 * from one original inset, across retold prose, into the next inset has both
 * endpoints in *an* original, and without the same-element test would resolve
 * to one contiguous original span silently swallowing every block between them
 * — text the reader never selected.
 */
export function selectionToOffsets(
  root: HTMLElement,
  sel: Selection | null,
  scope: RunScope = null,
): OffsetRange | null {
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (
    !root.contains(range.startContainer) ||
    !root.contains(range.endContainer)
  ) {
    return null;
  }
  const startRun = enclosingRun(range.startContainer, scope);
  const endRun = enclosingRun(range.endContainer, scope);
  if (!startRun || !endRun) return null;
  if (scope !== null && startRun.closest(scope) !== endRun.closest(scope)) {
    return null;
  }
  const a = offsetInRun(startRun, range.startContainer, range.startOffset);
  const b = offsetInRun(endRun, range.endContainer, range.endOffset);
  if (a === null || b === null) return null;
  const start = Math.min(a, b);
  const end = Math.max(a, b);
  return end > start ? { start, end } : null;
}

/** The caret DOM point under a viewport coordinate, across engines. */
function caretFromPoint(
  x: number,
  y: number,
): { node: Node; offset: number } | null {
  // Chrome/Safari (incl. iOS) expose caretRangeFromPoint; Firefox uses the
  // standardized caretPositionFromPoint. Either gives a collapsed text point.
  if (typeof document.caretRangeFromPoint === "function") {
    const range = document.caretRangeFromPoint(x, y);
    return range
      ? { node: range.startContainer, offset: range.startOffset }
      : null;
  }
  const pos = document.caretPositionFromPoint?.(x, y);
  return pos ? { node: pos.offsetNode, offset: pos.offset } : null;
}

/**
 * The document offset under a viewport point (tap-to-select), or null when it
 * lands outside any run. Pair with `normalizeSelection` to snap a tap anywhere
 * inside a word out to that word's whole-token span.
 */
export function offsetAtPoint(
  root: HTMLElement,
  x: number,
  y: number,
  scope: RunScope = null,
): number | null {
  const caret = caretFromPoint(x, y);
  if (!caret || !root.contains(caret.node)) return null;
  return pointToOffset(caret.node, caret.offset, scope);
}

/** Locate the run that owns `offset` and return its text node + local offset. */
export function offsetToPoint(
  root: HTMLElement,
  offset: number,
  scope: RunScope = null,
): DomPoint | null {
  const runs = root.querySelectorAll<HTMLElement>(
    scope === null ? "[data-start]" : `${scope} [data-start]`,
  );
  for (const run of runs) {
    const base = Number(run.dataset.start);
    if (Number.isNaN(base)) continue;
    const text = run.firstChild;
    if (!text || text.nodeType !== Node.TEXT_NODE) continue;
    const len = text.textContent?.length ?? 0;
    if (offset >= base && offset <= base + len) {
      return { node: text as Text, offset: offset - base };
    }
  }
  return null;
}

/** Re-select the `[start, end)` span in the DOM, best-effort. */
export function selectOffsets(
  root: HTMLElement,
  start: number,
  end: number,
  scope: RunScope = null,
): boolean {
  const a = offsetToPoint(root, start, scope);
  const b = offsetToPoint(root, end, scope);
  if (!a || !b) return false;
  const range = document.createRange();
  range.setStart(a.node, a.offset);
  range.setEnd(b.node, b.offset);
  const sel = window.getSelection();
  if (!sel) return false;
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
}
